import { Component } from '@verndale/core';
import { MASTHEAD_EVENTS } from '../coveo/modules/contractor-listing/helpers';
import {
  getAddressFromGeolocation,
  getStoredGeolocationCountryAndZip,
  updateGeolocationDataProperties
} from '../helpers/geolocation';
import { GeolocationType } from '../helpers/global-storage';
import { ExtendedWindowType } from '../helpers/global-types';
import DOMPurify from 'dompurify';
class CityListingMasthead extends Component {
  private validated: boolean;
  private validatedAddress: string;
  private locationData: GeolocationType | null;

  constructor(el: HTMLElement) {
    super(el);
    this.validated = false;
    this.validatedAddress = '';
    this.locationData = null;

    this.initAutocomplete();
  }

  setupDefaults() {
    this.dom = {
      addressForm: this.el.querySelector<HTMLFormElement>(
        '.city-listing-masthead__address-form'
      ),
      addressInput: this.el.querySelector<HTMLInputElement>(
        '.city-listing-masthead__address .field__input'
      ),
      addressSubmit: this.el.querySelector<HTMLButtonElement>(
        '.city-listing-masthead__submit-address'
      )
    };
  }

  addListeners() {
    (this.dom.addressForm as HTMLFormElement)?.addEventListener(
      'submit',
      this.handleAddressSubmit.bind(this)
    );

    (this.dom.addressInput as HTMLInputElement)?.addEventListener(
      'input',
      this.handleInputChange.bind(this)
    );

    window.addEventListener(
      MASTHEAD_EVENTS.changeTab,
      this.handleTabChange.bind(this)
    );
  }

  handleInputChange(e: Event) {
    const input = e.target as HTMLInputElement;

    (window as ExtendedWindowType).dataLayer.push({
      event: 'typeahead',
      event_category: 'typeahead',
      event_action: 'viewed',
      event_label: 'address'
    });

    if (input.value.length < 1) {
      this.validated = false;
    } else {
      if (input.value === this.validatedAddress) {
        this.validated = true;
      } else {
        this.validated = false;
      }
    }

    this.toggleSubmitButton();
  }

  handleTabChange(e: CustomEventInit) {
    const tab = e.detail.tab;
    (window as ExtendedWindowType).dataLayer.push({
      event_attributes: {
        zip_code: getStoredGeolocationCountryAndZip()?.zip
          ? getStoredGeolocationCountryAndZip()?.zip
          : null,
        element_text: tab
      },
      event: 'custom_click',
      event_category: 'find a contractor',
      event_label: '',
      event_group: 'tabs'
    });
  }

  toggleSubmitButton() {
    if (this.validated) {
      (this.dom.addressSubmit as HTMLButtonElement).disabled = false;
    } else {
      (this.dom.addressSubmit as HTMLButtonElement).disabled = true;
    }
  }

  fillInAddress(autocomplete: google.maps.places.Autocomplete) {
    // Get the place details from the autocomplete object.
    const place = autocomplete.getPlace();

    //get the zip from the address
    const zip = place.address_components?.find(component => {
      return component.types.includes('postal_code');
    })?.long_name;

    //get the country from the address
    const country = place.address_components?.find(component => {
      return component.types.includes('country');
    })?.short_name;

    //get latitude and longitude from the address

    const latitude = place.geometry?.location?.lat();
    const longitude = place.geometry?.location?.lng();

    this.locationData = {
      zip: zip as string,
      country: country as string,
      latitude,
      longitude,
      address: place.formatted_address
    };

    if (place.geometry) {
      this.validated = true;
      this.validatedAddress = (this.dom.addressInput as HTMLInputElement)
        ?.value as string;
    } else {
      this.validated = false;
    }

    (window as ExtendedWindowType).dataLayer.push({
      event: 'typeahead',
      event_category: 'typeahead',
      event_action: 'click',
      event_label: 'address'
    });
    this.toggleSubmitButton();
  }

  initAutocomplete() {
    if (window.google?.maps) {
      this.initMapsAutocomplete();
    } else {
      const maxAttempts = 20;
      let attempts = 0;
      const interval = setInterval(() => {
        attempts++;
        if (window.google?.maps) {
          this.initMapsAutocomplete();
          clearInterval(interval);
        } else if (attempts >= maxAttempts) {
          clearInterval(interval);
        }
      }, 500);
    }
  }

  initMapsAutocomplete() {
    const addressField = this.dom.addressInput as HTMLInputElement;

    const autocomplete = new window.google.maps.places.Autocomplete(
      addressField,
      {
        componentRestrictions: { country: ['us', 'ca'] },
        fields: ['formatted_address', 'address_components', 'geometry'],
        types: ['postal_code', 'route']
      }
    );

    // When the user selects an address from the drop-down, populate the
    // address fields in the form.
    autocomplete.addListener(
      'place_changed',
      this.fillInAddress.bind(this, autocomplete)
    );

    if (this.el.dataset.autoFillAddress) {
      const address = getAddressFromGeolocation();
      if (address) {
        (this.dom.addressInput as HTMLInputElement).value = address;
        this.validated = true;
        this.validatedAddress = address;
        this.toggleSubmitButton();
      }
    }
  }

  handleAddressSubmit(e: Event) {
    e.preventDefault();

    if (this.validated) {
      // Sanitize the base URL
      const baseUrl = DOMPurify.sanitize(this.el.dataset.url || '');

      const params = new URLSearchParams(window.location.search);
      // Sanitize the address value before setting it as a parameter
      const sanitizedAddress = DOMPurify.sanitize(
        (this.dom.addressInput as HTMLInputElement)?.value || ''
      );
      params.set('address', sanitizedAddress);

      if (this.locationData) {
        updateGeolocationDataProperties(this.locationData);
      }

      // Construct the final URL with sanitized components
      const queryString = params.toString();
      const finalUrl = `${baseUrl}${queryString ? `?${queryString}` : ''}`;

      window.location.href = finalUrl;
    }
  }
}

export default CityListingMasthead;
